import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import {
  Input,
  Select,
  Checkbox,
  RadioButton,
  TextArea,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function OHDICIntake() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const yesNoOptions = [
    {
      _id: "1",
      name: "Yes",
    },
    { _id: "0", name: "No" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <h4>Drop-In Center VOCA Intake Summary</h4>
              <i>
                Instructions: Complete upon initial youthinteraction with the
                VOCA program. If,a youth experiences multiple
                servicesepisode;please review the most recent Intake Summary
                and/or Addendums for continued accuracy. If no changes are
                needed to the Intake Summary, then sign and date the Intake
                Summary at the end of this document. If changes in either
                Victimization Type(s) or Special Classification(s) are noted,
                then a new Intake Summary should be completed.
              </i>
              <div className="text-center">
                <b>General Information</b>
              </div>
              <div className="container">
                <div className="row">
                  <div className="col-6">
                    <Input
                      name="DateOfIntake"
                      label="Date of Intake:"
                      type="date"
                      error={errors.DateOfIntake}
                      value={data.DateOfIntake["@value"]}
                      onChange={handleChange}
                      identical={compare("DateOfIntake")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="YouthStreet"
                      label="Youth Street Address:"
                      error={errors.YouthStreet}
                      value={data.YouthStreet["@value"]}
                      onChange={handleChange}
                      identical={compare("YouthStreet")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="YouthCity"
                      label="City:"
                      error={errors.YouthCity}
                      value={data.YouthCity["@value"]}
                      onChange={handleChange}
                      identical={compare("YouthCity")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="YouthState"
                      label="State:"
                      error={errors.YouthState}
                      value={data.YouthState["@value"]}
                      onChange={handleChange}
                      identical={compare("YouthState")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="YouthZip"
                      label="Zip:"
                      error={errors.YouthZip}
                      value={data.YouthZip["@value"]}
                      onChange={handleChange}
                      identical={compare("YouthZip")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="YouthPrimaryPhone"
                      label="Youth Primary Phone:"
                      error={errors.YouthPrimaryPhone}
                      value={data.YouthPrimaryPhone["@value"]}
                      onChange={handleChange}
                      identical={compare("YouthPrimaryPhone")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="YouthAltPhone"
                      label="Youth Alt. Phone:"
                      error={errors.YouthAltPhone}
                      value={data.YouthAltPhone["@value"]}
                      onChange={handleChange}
                      identical={compare("YouthAltPhone")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <br />
                <div className="row">
                  <div className="col">
                    Emergency Contact (Parent or Guardian)
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="EmergencyContactName"
                      label="Contact Name:"
                      error={errors.EmergencyContactName}
                      value={data.EmergencyContactName["@value"]}
                      onChange={handleChange}
                      identical={compare("EmergencyContactName")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="EmergencyContactRelationship"
                      label="Relationship:"
                      error={errors.EmergencyContactRelationship}
                      value={data.EmergencyContactRelationship["@value"]}
                      onChange={handleChange}
                      identical={compare("EmergencyContactRelationship")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="EmergencyContactStreet"
                      label="Contact Street Address:"
                      error={errors.EmergencyContactStreet}
                      value={data.EmergencyContactStreet["@value"]}
                      onChange={handleChange}
                      identical={compare("EmergencyContactStreet")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="EmergencyContactCity"
                      label="City:"
                      error={errors.EmergencyContactCity}
                      value={data.EmergencyContactCity["@value"]}
                      onChange={handleChange}
                      identical={compare("EmergencyContactCity")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="EmergencyContactState"
                      label="State:"
                      error={errors.EmergencyContactState}
                      value={data.EmergencyContactState["@value"]}
                      onChange={handleChange}
                      identical={compare("EmergencyContactState")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="EmergencyContactZip"
                      label="Zip:"
                      error={errors.EmergencyContactZip}
                      value={data.EmergencyContactZip["@value"]}
                      onChange={handleChange}
                      identical={compare("EmergencyContactZip")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Input
                      name="EmergencyContactPrimaryPhone"
                      label="Contact Primary Phone:"
                      error={errors.EmergencyContactPrimaryPhone}
                      value={data.EmergencyContactPrimaryPhone["@value"]}
                      onChange={handleChange}
                      identical={compare("EmergencyContactPrimaryPhone")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="EmergencyContactAltPhone"
                      label="Contact Alt. Phone:"
                      error={errors.EmergencyContactAltPhone}
                      value={data.EmergencyContactAltPhone["@value"]}
                      onChange={handleChange}
                      identical={compare("EmergencyContactAltPhone")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="text-center">
                <b>Youth Language Provisions</b>
              </div>
              <RadioButton
                name="ESLAccomodations"
                label="Does the Youth require English as a Second Language (ESL) accommodations?"
                options={yesNoOptions}
                error={errors.ESLAccomodations}
                value={data.ESLAccomodations["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.ESLAccomodations["@value"] === "1" && (
                <>
                  <Input
                    name="ESLAccomodationsDetails"
                    label="If yes, please describe type of accommodations to be provided:"
                    error={errors.ESLAccomodationsDetails}
                    value={data.ESLAccomodationsDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("ESLAccomodationsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              <RadioButton
                name="Allergies"
                label="Allergies?"
                options={yesNoOptions}
                error={errors.Allergies}
                value={data.Allergies["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.Allergies["@value"] === "1" && (
                <>
                  <Input
                    name="AllergiesDetails"
                    label="If yes, please describe allergies and reactions:"
                    error={errors.AllergiesDetails}
                    value={data.AllergiesDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("AllergiesDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              <br />
              <div className="text-center">
                <b>Youth Demographic Information</b>
              </div>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <b>Youth Race:</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="AmericanIndian"
                      label="American Indian/Alaska Native"
                      checked={data.AmericanIndian["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="White"
                      label="White Non-Latino/Caucasian"
                      checked={data.White["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Asian"
                      label="Asian"
                      checked={data.Asian["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="OtherRace"
                      label="Some Other Race:"
                      checked={data.OtherRace["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.OtherRace["@value"] === "true" && (
                      <>
                        <Input
                          name="OtherRaceDetails"
                          error={errors.OtherRaceDetails}
                          value={data.OtherRaceDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherRaceDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="AfricanAmerican"
                      label="Black/African American"
                      checked={data.AfricanAmerican["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="MultipleRaces"
                      label="Multiple races"
                      checked={data.MultipleRaces["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Hispanic"
                      label="Hispanic or Latino"
                      checked={data.Hispanic["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="RaceNotReported"
                      label="Not Reported/Declined to Disclose"
                      checked={data.RaceNotReported["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Hawaiian"
                      label="Native Hawaiian or Other Pacific Islander"
                      checked={data.Hawaiian["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <br />
                <div className="row">
                  <div className="col">
                    <b>Youth Gender Identification:</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Male"
                      label="Male"
                      checked={data.Male["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="OtherGender"
                      label="Other:"
                      checked={data.OtherGender["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.OtherGender["@value"] === "true" && (
                      <>
                        <Input
                          name="OtherGenderDetails"
                          error={errors.OtherGenderDetails}
                          value={data.OtherGenderDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherGenderDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Female"
                      label="Female"
                      checked={data.Female["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="GenderNotReported"
                      label="Not Reported/Declined to Disclose"
                      checked={data.GenderNotReported["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <br />
                <div className="row">
                  <div className="col">
                    <b>
                      Youth Victimization Type(s) (choose all that apply): Mark
                      which ones you have experienced.
                    </b>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="IdentifyTheft"
                      label="Identity Theft/Fraud/Financial Crime"
                      checked={data.IdentifyTheft["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="SexualAbuse"
                      label="Child Sexual Abuse/Assault"
                      checked={data.SexualAbuse["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="KidnappingByParent"
                      label="Kidnapping (by parent)"
                      checked={data.KidnappingByParent["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Homicide"
                      label="Survivors of Homicide Victims"
                      checked={data.Homicide["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="KidnappingByStranger"
                      label="Kidnapping (by stranger)"
                      checked={data.KidnappingByStranger["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="DomesticViolence"
                      label="Domestic and/or Family Violence"
                      checked={data.DomesticViolence["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="VehicularVictim"
                      label="Other Vehicular Victimization"
                      checked={data.VehicularVictim["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="TeenDating"
                      label="Teen Dating Victimization"
                      checked={data.TeenDating["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Arson"
                      label="Arson"
                      checked={data.Arson["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="DUIDWI"
                      label="DUI/DWI Incidents"
                      checked={data.DUIDWI["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Bullying"
                      label="Bullying (Cyber/Physical/Verbal)"
                      checked={data.Bullying["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="TerrorismDomestic"
                      label="Terrorism: Domestic"
                      checked={data.TerrorismDomestic["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Burglary"
                      label="Burglary"
                      checked={data.Burglary["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="TerrorismInternational"
                      label="Terrorism: International"
                      checked={data.TerrorismInternational["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="PhysicalAbuse"
                      label="Child Physical Abuse or Neglect"
                      checked={data.PhysicalAbuse["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="HumanTraffickingSex"
                      label="Human Trafficking: Sex"
                      checked={data.HumanTraffickingSex["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Robbery"
                      label="Robbery"
                      checked={data.Robbery["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="HumanTraffickingLabor"
                      label="Human Trafficking: Labor"
                      checked={data.HumanTraffickingLabor["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ChildPorn"
                      label="Child Pornography"
                      checked={data.ChildPorn["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="HateCrime"
                      label="Hate Crime"
                      checked={data.HateCrime["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Stalking"
                      label="Stalking/Harassment"
                      checked={data.Stalking["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="CourtOrder"
                      label="Violation of a Court (Protective) Order"
                      checked={data.CourtOrder["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <Checkbox
                      name="OtherVictim"
                      label="Other:"
                      checked={data.OtherVictim["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.OtherVictim["@value"] === "true" && (
                      <>
                        <Input
                          name="OtherVictimDetails"
                          error={errors.OtherVictimDetails}
                          value={data.OtherVictimDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherVictimDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                  </div>
                </div>
                <br />
                <div className="row">
                  <div className="col">
                    <b>
                      Youth Special Classification(s) (choose all that apply):
                    </b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <Checkbox
                      name="Deaf"
                      label="Deaf/Hard of Hearing"
                      checked={data.Deaf["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Homeless"
                      label="Homeless"
                      checked={data.Homeless["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="Disabilty"
                      label="Victims with Disabilities: Cognitive/Physical/Mental"
                      checked={data.Disabilty["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="Immigrant"
                      label="Immigrants/Refugees/Asylum Seekers"
                      checked={data.Immigrant["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="LimitedEnglish"
                      label="Victims with Limited English Proficiency"
                      checked={data.LimitedEnglish["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="LGBTQ"
                      label="LGBTQ"
                      checked={data.LGBTQ["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="OtherClass"
                      label="Other:"
                      checked={data.OtherClass["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.OtherClass["@value"] === "true" && (
                      <>
                        <Input
                          name="OtherClassDetails"
                          error={errors.OtherClassDetails}
                          value={data.OtherClassDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("OtherClassDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                  </div>
                </div>
                <br />
                <b>
                  <u>Needs List</u>
                </b>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <b>Hygiene</b>
                      <Checkbox
                        name="Deodorant"
                        label="Deodorant"
                        checked={data.Deodorant["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Shampoo"
                        label="Shampoo"
                        checked={data.Shampoo["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Conditioner"
                        label="Conditioner"
                        checked={data.Conditioner["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Soap"
                        label="Soap"
                        checked={data.Soap["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Lotion"
                        label="Lotion"
                        checked={data.Lotion["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Toothpaste"
                        label="Toothpaste"
                        checked={data.Toothpaste["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Toothbrush"
                        label="Toothbrush"
                        checked={data.Toothbrush["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Pads"
                        label="Pads"
                        checked={data.Pads["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Tampons"
                        label="Tampons"
                        checked={data.Tampons["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <TextArea
                        name="OtherHygieneDetails"
                        label="Other hygiene needs:"
                        error={errors.OtherHygieneDetails}
                        value={data.OtherHygieneDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherHygieneDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <b>School Supplies</b>
                      <Checkbox
                        name="Backpack"
                        label="Backpack"
                        checked={data.Backpack["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Pencils"
                        label="Pencils"
                        checked={data.Pencils["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Pens"
                        label="Pens"
                        checked={data.Pens["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Folders"
                        label="Folders"
                        checked={data.Folders["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Notebook"
                        label="Notebook"
                        checked={data.Notebook["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Crayons"
                        label="Crayons"
                        checked={data.Crayons["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="PencilSharpener"
                        label="Pencil sharpener"
                        checked={data.PencilSharpener["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Notecards"
                        label="Notecards"
                        checked={data.Notecards["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <TextArea
                        name="OtherSuppliesDetails"
                        label="Other school supplies:"
                        error={errors.OtherSuppliesDetails}
                        value={data.OtherSuppliesDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherSuppliesDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <b>Clothing</b>
                      <Checkbox
                        name="Socks"
                        label="Socks"
                        checked={data.Socks["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Underwear"
                        label="Underwear"
                        checked={data.Underwear["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Tshirts"
                        label="T-Shirts"
                        checked={data.Tshirts["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Gloves"
                        label="Gloves"
                        checked={data.Gloves["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Hat"
                        label="Hat/Scarf"
                        checked={data.Hat["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <TextArea
                        name="OtherClothingDetails"
                        label="Other clothing needs:"
                        error={errors.OtherClothingDetails}
                        value={data.OtherClothingDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherClothingDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <br />
                      <b>Transportation</b>
                      <Checkbox
                        name="BusRoute"
                        label="Bus Route"
                        checked={data.BusRoute["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="BusPass"
                        label="Bus pass"
                        checked={data.BusPass["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="CabNumber"
                        label="Cab Co number"
                        checked={data.CabNumber["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <TextArea
                        name="OtherTransportationDetails"
                        label="Other transportation needs:"
                        error={errors.OtherTransportationDetails}
                        value={data.OtherTransportationDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherTransportationDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <b>Referrals</b>
                      <Checkbox
                        name="Therapist"
                        label="Therapist"
                        checked={data.Therapist["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="CaseMgmt"
                        label="Case Mgmt"
                        checked={data.CaseMgmt["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Pantry"
                        label="Pantry/Food banks"
                        checked={data.Pantry["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Employment"
                        label="Employment"
                        checked={data.Employment["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="AODServices"
                        label="AOD services"
                        checked={data.AODServices["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="ChildrenServices"
                        label="Children Services"
                        checked={data.ChildrenServices["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="Shelters"
                        label="Shelters"
                        checked={data.Shelters["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <Checkbox
                        name="CrimeVictimServices"
                        label="Crime Victim Services"
                        checked={data.CrimeVictimServices["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <TextArea
                        name="OtherReferralsDetails"
                        label="Other referrals:"
                        error={errors.OtherReferralsDetails}
                        value={data.OtherReferralsDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherReferralsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <RadioButton
                name="ItemsDistributed"
                label="Were these items distributed?"
                options={yesNoOptions}
                error={errors.ItemsDistributed}
                value={data.ItemsDistributed["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.ItemsDistributed["@value"] === "0" && (
                <>
                  <Input
                    name="ItemsNotDistributedDetails"
                    label="If no: why?"
                    error={errors.ItemsNotDistributedDetails}
                    value={data.ItemsNotDistributedDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("ItemsNotDistributedDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              )}
              <div className="text-center">
                <h4>Drop-In Center Youth Rules Agreement</h4>
              </div>
              <p>
                Welcome to the SAFY Youth Drop-In Center. This center is for
                youth in crisis or for youth who need a place to go for safety.
                We, the staff, want to give you the support and power to make
                positive changes in your life.
              </p>
              <p>
                The center is open 7 days a week from10am to 8pm. Any closings
                will be posted and can be found on Facebook.
              </p>
              <p>
                All rides must be here by 7:45pm to pick up youth. Youth will be
                sent to the McDonalds on Market St. if rides are not here by
                closing. Youth may be picked up there.
              </p>
              <p>
                While you are here, you are encouraged to participate in
                available activities, group meetings, educational offerings, and
                meetings with staff.
              </p>
              <p>
                You may bring personal belongings to the Drop-In Center at your
                own risk. If you find something that does not belong to you,
                turn it into staff, you would appreciate the same courtesy.
              </p>
              <p>Respect all staff and other youth. Respect SAFY property.</p>
              <p>
                Follow the Golden Rule: "Do unto others as you would have them
                do unto you."
              </p>
              <p>We have personal hygiene items available upon request.</p>
              <p>You may use the Drop-In Center phone with permission.</p>
              <p>
                <b>Bullying, Disrespect, or Harassment</b>: If another youth is
                bothering you, disrespecting you, or harassing you, please talk
                to staff so that we can help to solve the problem before things
                get out of hand.
              </p>
              <p>
                We have <b>NO</b> tolerance for violence or threats of violence.
              </p>
              <p>
                <b>Dress code</b>: For girls and guys-Jeans, shorts, t-shirts,
                sweatshirts, and other casual clothing are permitted. Clothing
                must not contain sexual references, obscenities, alcohol or drug
                advertisementor otherwise offensive subject matter.
              </p>
              <p>
                The SAFY Youth Drop-In Center is a{" "}
                <b>Drug and Alcohol-Free Campus</b>. All youth and their
                belongings are subject to search upon entering of these
                premises.
              </p>
              <p>
                <b>NO WEAPONS</b> of any kind are permitted on the SAFY property
                including the hallway, and parking lot.
              </p>
              <p>
                <b>*NOTE</b>: Staff at the SAFY Youth Drop-In Center are
                mandated reporters. That means that when staff know or suspect
                that minors are being hurt or neglected,they must report this
                information to the proper authorities. Also,when staff know or
                suspect that anyone is going to hurt themselves or hurt others,
                this must also be reported to authorities. We will do our best
                to let you know when a report must be made but there may be
                times when we are unable to advise you of this.
              </p>
              <p>If you have any questions just ask staff!</p>
              <RadioButton
                name="WantCopy"
                label="Would you like a copy of this agreement for your records?"
                options={yesNoOptions}
                error={errors.WantCopy}
                value={data.WantCopy["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="text-center">
                <b>
                  SAFY Youth Drop-In Center
                  <br />
                  Youth Agreement
                </b>
              </div>
              <p>
                I have reviewed the expectations of the SAFY Youth Drop-In
                Center. I understand the expectations and that the purpose of
                the center is to provide safety as well as assist me in becoming
                responsible and healthy. To assist with the purpose, I agree to:
              </p>
              <ul>
                <li>
                  Allow SAFY Youth Drop-In Center to help me communicate with my
                  family/school.
                </li>
                <li>Attend counseling sessions if needed</li>
                <li>Meet with staff as needed</li>
                <li>Meet the expectations of the SAFY Youth Drop-In Center</li>
                <li>
                  Services may be provided via video-conferencing and all rules
                  and expectations of the YDC shall be followed using that
                  format.
                </li>
              </ul>
              <p>
                I understand that the information I give SAFY Youth Drop-In
                Center will be kept confidential. Information will only be
                released for mandated reporting purposes.
              </p>
              <p>
                I agree to and will do my best to meet expectations while I am
                visiting the SAFY Youth Drop-In Center.
              </p>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default OHDICIntake;
